<div class="article-listing-container">
  
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="spinner"></div>
    <p>Loading articles...</p>
  </div>
  
  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-state">
    <div class="error-icon">⚠️</div>
    <p class="error-message">{{ error }}</p>
    <button (click)="loadArticles()" class="retry-button">Retry</button>
  </div>
  
  <!-- Filter Dropdowns -->
  <div *ngIf="!loading && hasArticles" class="filters-container">
    <div class="filters-row">
      
      <!-- Discipline Filter -->
      <div *ngIf="filterOptions.disciplines.length > 0 && mode !== 'discipline'" class="filter-group">
        <label for="discipline-filter">Discipline/Course</label>
        <select 
          id="discipline-filter"
          [(ngModel)]="selectedFilters.discipline" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.discipline === 'All'">
          <option value="All" disabled selected hidden>Discipline / Course</option>
          <option *ngFor="let disc of filterOptions.disciplines" [value]="disc">
            {{ disc }}
          </option>
        </select>
      </div>
      
      <!-- Subject Filter -->
      <div *ngIf="filterOptions.subjects.length > 0 && mode !== 'subject'" class="filter-group">
        <label for="subject-filter">Subject</label>
        <select 
          id="subject-filter"
          [(ngModel)]="selectedFilters.subject" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.subject === 'All'">
          <option value="All" disabled selected hidden>Subject</option>
          <option *ngFor="let subj of filterOptions.subjects" [value]="subj">
            {{ subj }}
          </option>
        </select>
      </div>
      
      <!-- Topic Filter -->
      <div *ngIf="filterOptions.topics.length > 0 && mode !== 'topic'" class="filter-group">
        <label for="topic-filter">Topic</label>
        <select 
          id="topic-filter"
          [(ngModel)]="selectedFilters.topic" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.topic === 'All'">
          <option value="All" disabled selected hidden>Topic</option>
          <option *ngFor="let topic of filterOptions.topics" [value]="topic">
            {{ topic }}
          </option>
        </select>
      </div>
      
      <!-- Theme Filter -->
      <div *ngIf="filterOptions.themes.length > 0" class="filter-group">
        <label for="theme-filter">Theme</label>
        <select 
          id="theme-filter"
          [(ngModel)]="selectedFilters.theme" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.theme === 'All'">
          <option value="All" disabled selected hidden>Theme</option>
          <option *ngFor="let theme of filterOptions.themes" [value]="theme">
            {{ theme }}
          </option>
        </select>
      </div>
      
      <!-- Content Type Filter -->
      <div *ngIf="filterOptions.contentTypes.length > 0" class="filter-group">
        <label for="content-type-filter">Content Type</label>
        <select 
          id="content-type-filter"
          [(ngModel)]="selectedFilters.contentType" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.contentType === 'All'">
          <option value="All" disabled selected hidden>Content Type</option>
          <option *ngFor="let type of filterOptions.contentTypes" [value]="type">
            {{ type }}
          </option>
        </select>
      </div>
      
      <!-- Content Series Filter -->
      <div *ngIf="filterOptions.contentSeries.length > 0 && mode !== 'content-series'" class="filter-group">
        <label for="series-filter">Content Series</label>
        <select 
          id="series-filter"
          [(ngModel)]="selectedFilters.contentSeries" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.contentSeries === 'All'">
          <option value="All" disabled selected hidden>Content Series</option>
          <option *ngFor="let series of filterOptions.contentSeries" [value]="series">
            {{ series }}
          </option>
        </select>
      </div>
      
      <!-- Product Filter -->
      <div *ngIf="filterOptions.products.length > 0" class="filter-group">
        <label for="product-filter">Products</label>
        <select 
          id="product-filter"
          [(ngModel)]="selectedFilters.product" 
          (change)="onFilterChange()"
          class="filter-select"
          [class.placeholder]="selectedFilters.product === 'All'">
          <option value="All" disabled selected hidden>Products</option>
          <option *ngFor="let product of filterOptions.products" [value]="product">
            {{ product }}
          </option>
        </select>
      </div>
      
      <!-- Clear Filters Button -->
      <div *ngIf="hasActiveFilters" class="filter-group">
        <button (click)="clearAllFilters()" class="clear-filters-btn">
          Clear All
        </button>
      </div>
      
    </div>
  </div>
  
  <!-- Articles Grid -->
  <div *ngIf="!loading && hasArticles" class="articles-grid">
    <article-card
      *ngFor="let article of articles; trackBy: trackByArticleId"
      [article]="article"
      [variant]="cardVariant">
    </article-card>
  </div>
  
  <!-- Empty State -->
  <div *ngIf="!loading && !hasArticles && !error" class="empty-state">
    <div class="empty-icon">📭</div>
    <h3>No articles found</h3>
    <p>There are no articles matching your criteria: <strong>{{ filterValue }}</strong></p>
  </div>
  
  <!-- Pagination -->
  <div *ngIf="showPagination" class="pagination">
    
    <!-- Previous Page Group Button (only shown when there are previous pages) -->
    <button 
      *ngIf="canGoPrevGroup"
      class="page-nav page-group-nav"
      (click)="prevPageGroup()"
      [disabled]="!canGoPrevGroup"
      aria-label="Previous page group"
      title="Go to previous group of pages">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" [attr.fill]="canGoPrevGroup ? '#0600B4' : '#CCCCCC'"/>
      </svg>
    </button>
    
    <!-- Page Numbers -->
    <ng-container *ngIf="updateUrl; else stateOnlyPages">
      <!-- SEO-friendly links with href -->
      <a
        *ngFor="let pageNum of pageNumbers"
        [href]="buildPageUrl(pageNum)"
        (click)="goToPage(pageNum, $event)"
        class="page-number"
        [class.active]="pageNum === currentPage"
        [attr.aria-current]="pageNum === currentPage ? 'page' : null">
        {{ pageNum }}
      </a>
    </ng-container>
    
    <ng-template #stateOnlyPages>
      <!-- State-only buttons (no URL update) -->
      <button
        *ngFor="let pageNum of pageNumbers"
        class="page-number"
        [class.active]="pageNum === currentPage"
        (click)="goToPage(pageNum)"
        [attr.aria-current]="pageNum === currentPage ? 'page' : null">
        {{ pageNum }}
      </button>
    </ng-template>
    
    <!-- Next Page Group Button (only shown when there are more pages) -->
    <button 
      *ngIf="canGoNextGroup"
      class="page-nav page-group-nav"
      (click)="nextPageGroup()"
      [disabled]="!canGoNextGroup"
      aria-label="Next page group"
      title="Go to next group of pages">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" [attr.fill]="canGoNextGroup ? '#0600B4' : '#CCCCCC'"/>
      </svg>
    </button>
    
  </div>
  
</div>
